import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiUpload, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Users, UserPlus, Send, Shield, Trash2, Check, X, Plus, Trophy, Upload, FileSpreadsheet, Download, CalendarClock, Image as ImageIcon, Lock } from "lucide-react";
import RosterHandleSearch from "@/components/RosterHandleSearch";

const GAMES = ["Hell Let Loose"];
const ROLES = [
  "Officer", "Rifleman", "Assault", "Automatic Rifleman", "Medic", "Support",
  "Machine Gunner", "Anti-Tank", "Engineer", "Spotter", "Sniper",
  "Tank Commander", "Crewman", "Commander",
];
const COLORS = [
  "#00F0FF", "#FF007F", "#F4D03F", "#00FFB2", "#B084FF", "#7DF9FF",
  "#FF4500", "#39FF14", "#FF1493", "#FFD700", "#DC143C", "#00CED1",
  "#9400D3", "#FF6347", "#20B2AA", "#FF69B4", "#8A2BE2", "#00FA9A",
  "#FF8C00", "#4169E1", "#DA70D6", "#32CD32", "#E6194B", "#F58231",
];

export default function MyTeam() {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [invites, setInvites] = useState([]);
  const [players, setPlayers] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(true);

  // Register form
  const [regOpen, setRegOpen] = useState(false);
  const [reg, setReg] = useState({ name: "", tag: "", tier: "Mains", region: "eu", logo_color: COLORS[0] });

  // Roster editor
  const [rosterOpen, setRosterOpen] = useState(false);
  const [rosterTeam, setRosterTeam] = useState(null);
  const [roster, setRoster] = useState([{ handle: "", player_id: "" }]);

  // Invite form
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteTeam, setInviteTeam] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");

  // CSV stats import
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvTeam, setCsvTeam] = useState(null);
  const [csvText, setCsvText] = useState("");
  const [csvResult, setCsvResult] = useState(null);
  const [csvSubmitting, setCsvSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [t, i, fx, allTeamsRes] = await Promise.all([
        api.get("/me/teams"),
        api.get("/me/invites"),
        api.get("/fixtures?status=upcoming"),
        api.get("/teams"),
      ]);
      setTeams(t.data);
      setInvites(i.data);
      setFixtures(fx.data);
      setAllTeams(allTeamsRes.data);
      if (t.data.length) {
        const rosters = await Promise.all(
          t.data.map((mt) => api.get(`/players?team_id=${mt.id}`).then((r) => r.data).catch(() => []))
        );
        setPlayers(rosters.flat());
      } else {
        setPlayers([]);
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submitRegister = async () => {
    if (!reg.name || !reg.tag) { toast.error("Name and tag are required."); return; }
    try {
      await api.post("/teams/register", reg);
      toast.success(`Team registered as ${reg.tier} · pending admin approval.`);
      setRegOpen(false);
      setReg({ name: "", tag: "", tier: "Mains", region: "eu", logo_color: COLORS[0] });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Registration failed");
    }
  };

  const submitInvite = async () => {
    if (!inviteEmail) return;
    try {
      await api.post(`/teams/${inviteTeam.id}/invite`, { email: inviteEmail });
      toast.success(`Invite sent to ${inviteEmail}`);
      setInviteOpen(false);
      setInviteEmail("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invite failed");
    }
  };

  const respondInvite = async (inv, accept) => {
    try {
      await api.post(`/invites/${inv.id}/${accept ? "accept" : "decline"}`);
      toast.success(accept ? `You're now a rep for ${inv.team?.name}` : "Invite declined");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const openRosterEditor = (t) => {
    const existing = players.filter((p) => p.team_id === t.id);
    setRosterTeam(t);
    setRoster(existing.length ? existing.map((p) => ({
      handle: p.handle || "",
      player_id: p.player_id || "",
      email: p.email || "",
      is_rep: !!p.is_rep,
    })) : [{ handle: "", player_id: "", email: "", is_rep: false }]);
    setRosterOpen(true);
  };

  const openCsvImport = (t) => {
    setCsvTeam(t);
    setCsvText("handle,kills,deaths,matches,winrate\n");
    setCsvResult(null);
    setCsvOpen(true);
  };

  const submitCsv = async () => {
    if (!csvText.trim()) { toast.error("Paste your CSV rows first."); return; }
    setCsvSubmitting(true);
    setCsvResult(null);
    try {
      const { data } = await api.post(`/teams/${csvTeam.id}/roster-stats-import`, { csv: csvText });
      setCsvResult(data);
      toast.success(`Imported stats for ${data.updated} player${data.updated === 1 ? "" : "s"}${data.skipped?.length ? ` · ${data.skipped.length} skipped` : ""}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setCsvSubmitting(false);
    }
  };

  const uploadTeamLogo = async (teamId, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Pick an image file."); return; }
    if (file.size > 3 * 1024 * 1024) { toast.error("Image too large (max 3MB)."); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      await apiUpload(`/teams/${teamId}/logo`, fd);
      toast.success("Team logo updated.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const uploadRosterCsv = async (teamId, file) => {
    if (!file) return;
    const fn = file.name.toLowerCase();
    const isCsv = fn.endsWith(".csv");
    const isCvc = fn.endsWith(".cvc");
    const isPdf = fn.endsWith(".pdf");
    if (!isCsv && !isCvc && !isPdf) { toast.error("Pick a CSV, CVC or PDF file."); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("File too large (max 15MB)."); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await apiUpload(`/teams/${teamId}/roster-csv`, fd);
      const note = data.warnings?.length ? ` · ${data.warnings.join(" ")}` : "";
      toast.success(`Roster ${data.kind?.toUpperCase()} parsed — ${data.imported} players imported${data.with_url ? ` (${data.verified}/${data.with_url} HLL-verified)` : ""}${note}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const exportRosterCsv = (t) => {
    const rows = players.filter((p) => p.team_id === t.id);
    if (rows.length === 0) { toast.error("Empty roster — nothing to export."); return; }
    const header = ["player_name", "player_id", "kills", "deaths", "matches", "winrate"];
    const csvLines = [header.join(",")];
    for (const p of rows) {
      const fields = [
        (p.handle || "").replace(/"/g, "\"\""),
        (p.player_id || "").replace(/"/g, "\"\""),
        p.hll_kills ?? p.kills ?? "",
        p.hll_deaths ?? p.deaths ?? "",
        p.hll_matches ?? p.matches ?? "",
        p.hll_winrate ?? "",
      ];
      csvLines.push(fields.map((f) => (String(f).includes(",") ? `"${f}"` : f)).join(","));
    }
    const blob = new Blob([csvLines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${t.tag || "roster"}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`${rows.length} players exported.`);
  };

  const submitRoster = async () => {
    const valid = roster.filter((r) => r.handle.trim()).map((r) => ({
      handle: r.handle.trim(),
      player_id: (r.player_id || "").trim(),
      is_rep: !!r.is_rep,
      email: r.is_rep ? (r.email || "").trim() : undefined,
    }));
    if (valid.length === 0) { toast.error("Add at least one player."); return; }
    const missingEmail = valid.find((r) => r.is_rep && !r.email);
    if (missingEmail) { toast.error(`Rep '${missingEmail.handle}' needs an email so we can grant clan rep access.`); return; }
    try {
      const { data } = await api.post(`/teams/${rosterTeam.id}/roster`, { players: valid });
      const reps = data.rep_promotions || [];
      const granted = reps.filter((r) => r.status === "granted").length;
      const invited = reps.filter((r) => r.status === "invited").length;
      let note = "";
      if (granted) note += ` · ${granted} rep${granted > 1 ? "s" : ""} promoted`;
      if (invited) note += ` · ${invited} rep invite${invited > 1 ? "s" : ""} pending`;
      toast.success(`Roster submitted · ${data.roster_size} players locked in${note}`);
      setRosterOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  if (loading) return <div className="text-gray-500">Loading your locker…</div>;

  const ownedTeam = teams.find((t) => t.role === "owner");
  const repTeams = teams.filter((t) => t.role === "rep");

  return (
    <div className="space-y-10" data-testid="my-team-root">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-cyan-400 font-mono tracking-[0.3em] text-xs mb-2">/ MEMBER LOCKER</div>
          <h1 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter uppercase">My Team</h1>
          <p className="text-gray-400 mt-2 max-w-xl text-sm">
            Register your own franchise, add co-manager reps, and submit the starting roster.
          </p>
        </div>
        {!ownedTeam && (
          <Dialog open={regOpen} onOpenChange={setRegOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="register-team-btn"
                className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest font-bold"
              >
                <Plus className="w-4 h-4 mr-2" /> Register your team
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none">
              <DialogHeader>
                <DialogTitle className="font-heading uppercase tracking-tight">New franchise</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-400">Team name</Label>
                  <Input
                    data-testid="reg-name"
                    value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })}
                    className="rounded-none bg-[#05050A] border-[#232330] mt-2"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-gray-400">Tag (2-6)</Label>
                    <Input
                      data-testid="reg-tag"
                      value={reg.tag}
                      maxLength={6}
                      onChange={(e) => setReg({ ...reg, tag: e.target.value.toUpperCase() })}
                      className="rounded-none bg-[#05050A] border-[#232330] mt-2 uppercase font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-gray-400">Tier</Label>
                    <Select value={reg.tier} onValueChange={(v) => setReg({ ...reg, tier: v })}>
                      <SelectTrigger data-testid="reg-tier" className="rounded-none bg-[#05050A] border-[#232330] mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#101015] border-[#232330] text-white">
                        <SelectItem value="Mains">Mains · 150 roster</SelectItem>
                        <SelectItem value="Lite">Lite · 100 roster</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-400">Region</Label>
                  <div className="flex gap-2 mt-2" data-testid="reg-region-group">
                    {[
                      { v: "eu", label: "EU" },
                      { v: "na", label: "NA" },
                      { v: "sa", label: "SA" },
                      { v: "oce", label: "OCE" },
                    ].map((r) => (
                      <button
                        key={r.v}
                        onClick={() => setReg({ ...reg, region: r.v })}
                        data-testid={`reg-region-${r.v}`}
                        className={`flex-1 border py-2 text-xs font-mono uppercase tracking-widest transition-colors ${
                          reg.region === r.v ? "border-cyan-400 text-cyan-400 bg-cyan-400/10" : "border-[#232330] text-gray-500 hover:text-white"
                        }`}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-400">Colorway</Label>
                  <div className="grid grid-cols-8 gap-2 mt-2">
                    {COLORS.map((c) => (
                      <button
                        key={c}
                        onClick={() => setReg({ ...reg, logo_color: c })}
                        className={`w-8 h-8 border-2 transition-transform ${
                          reg.logo_color === c ? "scale-110 border-white" : "border-[#232330]"
                        }`}
                        style={{ backgroundColor: c }}
                        data-testid={`reg-color-${c.slice(1)}`}
                      />
                    ))}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Custom:</span>
                    <input
                      type="color"
                      value={reg.logo_color}
                      onChange={(e) => setReg({ ...reg, logo_color: e.target.value })}
                      data-testid="reg-color-custom"
                      className="w-10 h-8 border border-[#232330] bg-[#05050A] cursor-pointer"
                    />
                    <span className="text-[10px] font-mono text-gray-500">{reg.logo_color}</span>
                  </div>
                </div>
                <Button
                  onClick={submitRegister}
                  data-testid="reg-submit"
                  className="w-full rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest font-bold"
                >
                  Lock franchise
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Invites */}
      {invites.length > 0 && (
        <section data-testid="my-invites">
          <div className="text-[10px] font-mono tracking-widest text-[#F4D03F] uppercase mb-3">
            Pending invites · {invites.length}
          </div>
          <div className="space-y-2">
            {invites.map((inv) => (
              <div
                key={inv.id}
                className="border border-[#F4D03F]/50 bg-[#F4D03F]/5 p-4 flex flex-wrap items-center justify-between gap-3"
              >
                <div>
                  <div className="font-heading font-bold uppercase tracking-tight">
                    Join <span style={{ color: inv.team?.logo_color }}>{inv.team?.name}</span> as a rep
                  </div>
                  <div className="text-xs text-gray-500 font-mono">
                    {inv.team?.tag} · {inv.team?.game}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => respondInvite(inv, true)}
                    data-testid={`accept-invite-${inv.id}`}
                    className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest text-xs font-bold"
                  >
                    <Check className="w-3.5 h-3.5 mr-2" /> Accept
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => respondInvite(inv, false)}
                    data-testid={`decline-invite-${inv.id}`}
                    className="rounded-none border-[#232330] uppercase tracking-widest text-xs"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Rep Fixture Portal — upcoming matches for the user's teams */}
      {teams.length > 0 && (() => {
        const myTeamIds = new Set(teams.map((t) => t.id));
        const myFx = fixtures.filter((f) => myTeamIds.has(f.home_team_id) || myTeamIds.has(f.away_team_id))
                             .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
        return (
          <section data-testid="rep-fixture-portal" className="space-y-3">
            <div className="text-[10px] font-mono tracking-widest text-[#F4D03F] uppercase px-1 flex items-center gap-2">
              <CalendarClock className="w-3.5 h-3.5" /> Your upcoming matches · {myFx.length}
            </div>
            {myFx.length === 0 ? (
              <div className="border border-[#232330] p-6 text-center text-gray-500 font-mono text-xs">
                No matches on your board yet — admins will schedule them soon.
              </div>
            ) : (
              <div className="grid gap-2">
                {myFx.slice(0, 6).map((f) => {
                  const home = allTeams.find((t) => t.id === f.home_team_id) || teams.find((t) => t.id === f.home_team_id);
                  const away = allTeams.find((t) => t.id === f.away_team_id) || teams.find((t) => t.id === f.away_team_id);
                  const homeIsMine = myTeamIds.has(f.home_team_id);
                  const awayIsMine = myTeamIds.has(f.away_team_id);
                  return (
                    <div key={f.id} data-testid={`portal-fx-${f.id}`}
                         className="border border-[#232330] bg-[#101015] p-4 flex flex-wrap items-center justify-between gap-3 hover:border-[#F4D03F]/60 transition-colors">
                      <div>
                        <div className="text-[10px] font-mono tracking-widest text-gray-500 uppercase">
                          {new Date(f.scheduled_at).toLocaleString()} · WK{f.week}{f.map ? ` · ${f.map}` : ""}
                        </div>
                        <div className="font-heading text-lg font-black uppercase tracking-tight mt-1">
                          <span className={homeIsMine ? "text-[#F4D03F]" : "text-white"}>{home?.name || "TBD"}</span>
                          <span className="text-gray-500 mx-3">vs</span>
                          <span className={awayIsMine ? "text-[#F4D03F]" : "text-white"}>{away?.name || "TBD"}</span>
                        </div>
                      </div>
                      <Link
                        to="/fixtures"
                        data-testid={`portal-submit-${f.id}`}
                        className="rounded-none bg-[#FF007F] hover:bg-pink-400 text-white uppercase tracking-widest text-xs font-bold px-4 py-2"
                      >
                        Submit result →
                      </Link>
                    </div>
                  );
                })}
                {myFx.length > 6 && (
                  <Link to="/fixtures" className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 hover:text-cyan-300 text-center py-2">
                    View all {myFx.length} matches →
                  </Link>
                )}
              </div>
            )}
          </section>
        );
      })()}

      {/* Teams */}
      {teams.length === 0 ? (
        <div className="border border-[#232330] p-10 text-center">
          <Users className="w-10 h-10 mx-auto text-gray-600 mb-4" />
          <div className="font-heading font-black text-xl uppercase tracking-tight">No team yet</div>
          <p className="text-gray-500 text-sm mt-2 max-w-md mx-auto">
            Register your own franchise to compete on the circuit. You can invite up to 12 players and add rep co-managers.
          </p>
        </div>
      ) : (
        <section className="space-y-4">
          {teams.map((t) => {
            const roster = players.filter((p) => p.team_id === t.id);
            const isOwner = t.role === "owner";
            return (
              <div
                key={t.id}
                data-testid={`myteam-${t.tag}`}
                className="border border-[#232330] bg-[#101015]"
                style={{ borderLeft: `4px solid ${t.logo_color}` }}
              >
                <div className="p-6">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      {t.logo_url ? (
                        <img src={t.logo_url} alt={t.name}
                             className="w-14 h-14 object-cover border border-[#232330]" />
                      ) : (
                        <div className="w-14 h-14 border border-dashed border-[#232330] flex items-center justify-center text-gray-600">
                          <ImageIcon className="w-5 h-5" />
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            className={`text-[10px] font-mono tracking-widest uppercase font-bold px-2 py-0.5 border ${
                              isOwner ? "border-cyan-400 text-cyan-400" : "border-[#F4D03F] text-[#F4D03F]"
                            }`}
                          >
                            {isOwner ? "OWNER" : "REP"}
                          </span>
                          <span className="text-xs font-mono text-gray-500 tracking-widest">{t.tag} · {t.game} · {t.tier || "Mains"}</span>
                        </div>
                        <div className="font-heading text-3xl font-black uppercase tracking-tight">{t.name}</div>
                        {t.manager && <div className="text-xs text-gray-500 font-mono mt-1">Managed by {t.manager}</div>}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {t.roster_locked && (
                        <span
                          data-testid={`myteam-locked-${t.tag}`}
                          className="text-xs font-mono uppercase tracking-widest text-[#F4D03F] border border-[#F4D03F] bg-[#F4D03F]/10 px-3 py-2 flex items-center gap-1"
                          title={t.roster_locked_at ? `Roster locked ${new Date(t.roster_locked_at).toLocaleString()} — contact an admin to unlock.` : "Roster locked by admin"}
                        >
                          <Lock className="w-3 h-3" /> Roster locked
                        </span>
                      )}
                      <label
                        data-testid={`upload-logo-${t.tag}`}
                        className="cursor-pointer text-xs font-mono uppercase tracking-widest text-gray-400 hover:text-cyan-400 border border-[#232330] hover:border-cyan-400 px-3 py-2 flex items-center gap-1"
                      >
                        <Upload className="w-3 h-3" /> Logo
                        <input type="file" accept="image/*" className="hidden"
                               onChange={(e) => uploadTeamLogo(t.id, e.target.files?.[0])} />
                      </label>
                      <label
                        data-testid={`upload-roster-csv-${t.tag}`}
                        className={`cursor-pointer text-xs font-mono uppercase tracking-widest border px-3 py-2 flex items-center gap-1 ${
                          t.roster_locked
                            ? "text-gray-600 border-[#232330] opacity-40 cursor-not-allowed"
                            : "text-gray-400 hover:text-[#F4D03F] border-[#232330] hover:border-[#F4D03F]"
                        }`}
                        title={t.roster_locked ? "Roster is season-locked — contact an admin" : ""}
                      >
                        <FileSpreadsheet className="w-3 h-3" /> {t.roster_csv_name ? "Replace roster" : "Upload roster (CSV/CVC/PDF)"}
                        <input type="file" accept=".csv,.cvc,text/csv,.pdf,application/pdf" className="hidden"
                               disabled={t.roster_locked}
                               onChange={(e) => uploadRosterCsv(t.id, e.target.files?.[0])} />
                      </label>
                      {roster.length > 0 && (
                        <a
                          href={`${API}/teams/${t.id}/roster.cvc`}
                          data-testid={`export-roster-cvc-${t.tag}`}
                          className="text-xs font-mono uppercase tracking-widest text-cyan-400 border border-cyan-400/40 hover:border-cyan-400 hover:bg-cyan-400/10 px-3 py-2 flex items-center gap-1"
                          title="Download the current roster in the league's CVC file format"
                        >
                          <Download className="w-3 h-3" /> Export CVC
                        </a>
                      )}
                      <Button
                        onClick={() => openRosterEditor(t)}
                        disabled={t.roster_locked}
                        data-testid={`edit-roster-${t.tag}`}
                        title={t.roster_locked ? "Roster is season-locked — contact an admin" : ""}
                        className="rounded-none bg-[#FF007F] hover:bg-pink-400 text-white uppercase tracking-widest text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Users className="w-3.5 h-3.5 mr-2" /> {roster.length ? "Edit roster" : "Submit roster"}
                      </Button>
                      <Button
                        onClick={() => { setInviteTeam(t); setInviteOpen(true); setInviteEmail(""); }}
                        data-testid={`invite-rep-${t.tag}`}
                        variant="outline"
                        className="rounded-none border-[#232330] hover:border-cyan-400 uppercase tracking-widest text-xs"
                      >
                        <UserPlus className="w-3.5 h-3.5 mr-2" /> Invite rep
                      </Button>
                      {roster.length > 0 && (
                        <>
                          <Button
                            onClick={() => openCsvImport(t)}
                            data-testid={`csv-import-${t.tag}`}
                            variant="outline"
                            className="rounded-none border-[#232330] hover:border-[#F4D03F] hover:text-[#F4D03F] uppercase tracking-widest text-xs"
                          >
                            <FileSpreadsheet className="w-3.5 h-3.5 mr-2" /> Import stats CSV
                          </Button>
                          <Button
                            onClick={() => exportRosterCsv(t)}
                            data-testid={`csv-export-${t.tag}`}
                            variant="outline"
                            className="rounded-none border-[#232330] hover:border-cyan-400 hover:text-cyan-400 uppercase tracking-widest text-xs"
                          >
                            <Download className="w-3.5 h-3.5 mr-2" /> Export roster CSV
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-6">
                    {[
                      { l: "Reps", v: (t.reps?.length || 0) },
                      { l: "Roster", v: roster.length },
                      { l: "W", v: t.wins || 0 },
                      { l: "L", v: t.losses || 0 },
                      { l: "PTS", v: t.points || 0 },
                    ].map((s) => (
                      <div key={s.l} className="border border-[#232330] p-3">
                        <div className="text-[9px] text-gray-500 font-mono tracking-widest">{s.l}</div>
                        <div className="font-heading font-black text-xl">{s.v}</div>
                      </div>
                    ))}
                  </div>

                  {roster.length > 0 && (
                    <div className="mt-5 pt-5 border-t border-[#232330]">
                      <div className="text-[10px] font-mono tracking-widest text-gray-500 uppercase mb-2">Roster</div>
                      <div className="flex flex-wrap gap-2">
                        {roster.map((p) => (
                          <span key={p.id} className="text-xs font-mono px-2 py-1 border border-[#232330] text-gray-200">
                            {p.handle} <span className="text-gray-500">· {p.role}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </section>
      )}

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">
              Invite a rep to {inviteTeam?.name}
            </DialogTitle>
          </DialogHeader>
          <Label className="text-xs uppercase tracking-widest text-gray-400 mt-2">Registered member email</Label>
          <Input
            data-testid="invite-email"
            type="email" value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="teammate@example.com"
            className="rounded-none bg-[#05050A] border-[#232330]"
          />
          <Button
            onClick={submitInvite}
            data-testid="invite-submit"
            className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest font-bold"
          >
            <Send className="w-3.5 h-3.5 mr-2" /> Send invite
          </Button>
        </DialogContent>
      </Dialog>

      {/* CSV Stats Import dialog */}
      <Dialog open={csvOpen} onOpenChange={(o) => { setCsvOpen(o); if (!o) setCsvResult(null); }}>
        <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">
              Bulk stats import · {csvTeam?.name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500 font-mono">
            Paste one player per line as <span className="text-cyan-400">handle,kills,deaths,matches,winrate</span>.
            Header row optional. Handles must match your roster (case-insensitive).
          </p>
          <Textarea
            data-testid="my-team-csv-textarea"
            rows={10}
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            placeholder={"handle,kills,deaths,matches,winrate\nZeroFrost,412,188,54,61\nGlows,301,220,47,52"}
            className="rounded-none bg-[#05050A] border-[#232330] font-mono text-xs"
          />
          {csvResult && (
            <div data-testid="my-team-csv-result" className="border border-[#232330] p-3 text-xs font-mono space-y-1">
              <div className="text-cyan-400">
                Updated: <span className="text-white">{csvResult.updated}</span>
              </div>
              {csvResult.skipped?.length > 0 && (
                <div className="text-[#F4D03F]">
                  Skipped ({csvResult.skipped.length}):{" "}
                  <span className="text-gray-400">{csvResult.skipped.join(", ")}</span>
                </div>
              )}
              {csvResult.details?.length > 0 && (
                <div className="pt-2 border-t border-[#232330] space-y-0.5">
                  {csvResult.details.map((d) => (
                    <div key={d.handle} className="text-gray-400">
                      <span className="text-white">{d.handle}</span> · {d.stats.kills ?? "-"}K / {d.stats.deaths ?? "-"}D
                      {d.stats.kd ? ` · ${d.stats.kd} KD` : ""}
                      {d.stats.winrate != null ? ` · ${d.stats.winrate}% WR` : ""}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="flex gap-2 pt-2 border-t border-[#232330]">
            <Button
              onClick={submitCsv}
              disabled={csvSubmitting}
              data-testid="my-team-csv-submit"
              className="flex-1 rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest font-bold disabled:opacity-50"
            >
              <Upload className="w-3.5 h-3.5 mr-2" /> {csvSubmitting ? "Importing…" : "Import stats"}
            </Button>
            <Button
              variant="outline"
              onClick={() => setCsvOpen(false)}
              data-testid="my-team-csv-cancel"
              className="rounded-none border-[#232330] uppercase tracking-widest"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Roster editor dialog */}
      <Dialog open={rosterOpen} onOpenChange={setRosterOpen}>
        <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">
              {rosterTeam?.name} · roster
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 pb-1">
              Max 4 reps per team (including team owner). Reps get the <span className="text-cyan-400">clan rep</span> role on the site.
              <span className="block mt-1 normal-case tracking-normal text-[11px] text-gray-400">
                <span className="text-cyan-400">Tip:</span> start typing a handle — matches from HLL Records auto-fill the player&nbsp;ID.
              </span>
            </div>
            {roster.map((p, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_1.2fr_auto_36px] gap-2 items-end" data-testid={`roster-row-${i}`}>
                <div>
                  {i === 0 && <Label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 block">Player name</Label>}
                  <RosterHandleSearch
                    testId={`roster-handle-${i}`}
                    value={p.handle}
                    onChange={(v) => {
                      const next = [...roster]; next[i] = { ...next[i], handle: v };
                      setRoster(next);
                    }}
                    onPick={(r) => {
                      const next = [...roster];
                      next[i] = {
                        ...next[i],
                        handle: r.handle || next[i].handle,
                        player_id: r.player_id || next[i].player_id,
                        hll_profile_url: r.hll_profile_url || next[i].hll_profile_url || "",
                      };
                      setRoster(next);
                      toast.success(`Filled ${r.handle} from HLL Records${r.hll_verified ? " · verified" : ""}`);
                    }}
                    placeholder="ZeroFrost"
                  />
                </div>
                <div>
                  {i === 0 && <Label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 block">Player ID</Label>}
                  <Input
                    data-testid={`roster-player-id-${i}`}
                    value={p.player_id}
                    onChange={(e) => {
                      const next = [...roster]; next[i] = { ...next[i], player_id: e.target.value };
                      setRoster(next);
                    }}
                    placeholder="76561198…"
                    className="rounded-none bg-[#05050A] border-[#232330] font-mono"
                  />
                </div>
                <div>
                  {i === 0 && <Label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 block">Email (for reps)</Label>}
                  <Input
                    data-testid={`roster-email-${i}`}
                    type="email"
                    value={p.email || ""}
                    onChange={(e) => {
                      const next = [...roster]; next[i] = { ...next[i], email: e.target.value };
                      setRoster(next);
                    }}
                    placeholder="rep@team.gg"
                    disabled={!p.is_rep}
                    className="rounded-none bg-[#05050A] border-[#232330] font-mono disabled:opacity-40"
                  />
                </div>
                <label
                  className={`h-9 px-3 border flex items-center gap-1.5 cursor-pointer transition-colors text-[10px] font-mono uppercase tracking-widest ${
                    p.is_rep
                      ? "bg-[#00FFB2] text-black border-[#00FFB2] font-bold"
                      : "text-gray-500 border-[#232330] hover:border-[#00FFB2] hover:text-[#00FFB2]"
                  }`}
                  data-testid={`roster-rep-toggle-${i}`}
                >
                  <input
                    type="checkbox"
                    checked={!!p.is_rep}
                    onChange={(e) => {
                      const willBeRep = e.target.checked;
                      const currentRepCount = roster.filter((r, j) => j !== i && r.is_rep).length;
                      // Owner counts as 1 rep implicitly; max 4 = owner + 3 additional
                      if (willBeRep && currentRepCount >= 3) {
                        toast.error("Max 4 reps per team (including owner). Un-flag another rep first.");
                        return;
                      }
                      const next = [...roster];
                      next[i] = { ...next[i], is_rep: willBeRep };
                      setRoster(next);
                    }}
                    className="w-3 h-3 accent-emerald-400"
                  />
                  Rep
                </label>
                <button
                  onClick={() => setRoster(roster.filter((_, j) => j !== i))}
                  data-testid={`roster-remove-${i}`}
                  className="h-9 border border-[#232330] hover:border-red-500 hover:text-red-500 text-gray-500 flex items-center justify-center"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {roster.length < (rosterTeam?.roster_cap || 12) && (
              <button
                onClick={() => setRoster([...roster, { handle: "", player_id: "", email: "", is_rep: false }])}
                data-testid="roster-add-row"
                className="w-full py-2 border border-dashed border-[#232330] hover:border-cyan-400 hover:text-cyan-400 text-gray-500 text-xs uppercase tracking-widest font-mono transition-colors"
              >
                <Plus className="w-3.5 h-3.5 inline mr-2" /> Add player ({roster.length}/{rosterTeam?.roster_cap || 12})
              </button>
            )}
          </div>
          <div className="flex gap-2 pt-2 border-t border-[#232330]">
            <Button
              onClick={submitRoster}
              data-testid="roster-submit"
              className="flex-1 rounded-none bg-[#FF007F] hover:bg-pink-400 text-white uppercase tracking-widest font-bold"
            >
              Submit roster
            </Button>
            <Button
              variant="outline"
              onClick={() => setRosterOpen(false)}
              className="rounded-none border-[#232330] uppercase tracking-widest"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
